import type { LoginContext } from "@/modules/auth/services/auth.api";

// Keep these key strings centralized here to avoid scattered literals.
// NOTE: We intentionally do not import constants from `auth.api.ts` to avoid circular deps.
const AUTH_TOKEN_KEY = "auth_token";
const AUTH_PENDING_CONTEXT_SELECTION_KEY = "auth_requires_context_selection";
const AUTH_CONTEXTS_KEY = "auth_contexts";
const AUTH_PROFILE_SYNC_KEY = "auth_profile_last_synced";
const AUTH_SELECTION_TOKEN_KEY = "auth_selection_token";
const AUTH_CUSTOMER_KEY = "auth_customer";
const AUTH_ACTIVE_CONTEXT_KEY = "auth_active_context";

function hasLocalStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

export function readString(key: string): string | null {
  if (!hasLocalStorage()) return null;
  try {
    return localStorage.getItem(key);
  } catch (_e) {
    return null;
  }
}

export function writeStringIfChanged(key: string, value: string) {
  if (!hasLocalStorage()) return;
  try {
    const existing = localStorage.getItem(key);
    if (existing === value) return;
    localStorage.setItem(key, value);
  } catch (_e) {
    // ignore
  }
}

export function readJson<T>(key: string): T | null {
  const raw = readString(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch (_e) {
    return null;
  }
}

export function writeJsonIfChanged(key: string, value: any) {
  if (!hasLocalStorage()) return;
  try {
    const next = JSON.stringify(value);
    const existing = localStorage.getItem(key);
    if (existing === next) return;
    localStorage.setItem(key, next);
  } catch (_e) {
    // ignore
  }
}

export function remove(key: string) {
  if (!hasLocalStorage()) return;
  try {
    localStorage.removeItem(key);
  } catch (_e) {
    // ignore
  }
}

export function removeMany(keys: string[]) {
  keys.forEach((k) => remove(k));
}

export function getAuthToken() {
  return readString(AUTH_TOKEN_KEY);
}

export function setAuthToken(token: string) {
  if (!token) return;
  writeStringIfChanged(AUTH_TOKEN_KEY, token);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("auth-changed"));
  }
}

export function clearAuthToken() {
  remove(AUTH_TOKEN_KEY);
}

export function getActiveContext(): Partial<LoginContext> | null {
  return readJson<Partial<LoginContext>>(AUTH_ACTIVE_CONTEXT_KEY);
}

export function setActiveContext(context: Partial<LoginContext>) {
  if (!context || typeof context !== "object") return;
  writeJsonIfChanged(AUTH_ACTIVE_CONTEXT_KEY, context);
}

export function clearActiveContext() {
  remove(AUTH_ACTIVE_CONTEXT_KEY);
}

export function getContexts(): LoginContext[] | null {
  return readJson<LoginContext[]>(AUTH_CONTEXTS_KEY);
}

export function setContexts(contexts: LoginContext[]) {
  if (!Array.isArray(contexts)) return;
  writeJsonIfChanged(AUTH_CONTEXTS_KEY, contexts);
}

export function clearContexts() {
  remove(AUTH_CONTEXTS_KEY);
}

export function getSelectionToken() {
  return readString(AUTH_SELECTION_TOKEN_KEY);
}

export function setSelectionToken(token: string) {
  if (!token) return;
  writeStringIfChanged(AUTH_SELECTION_TOKEN_KEY, token);
}

export function clearSelectionToken() {
  remove(AUTH_SELECTION_TOKEN_KEY);
}

export function getCustomer<T = any>(): T | null {
  return readJson<T>(AUTH_CUSTOMER_KEY);
}

export function setCustomer(value: any) {
  if (!value) return;
  writeJsonIfChanged(AUTH_CUSTOMER_KEY, value);
}

export function clearCustomer() {
  remove(AUTH_CUSTOMER_KEY);
}

export function getProfileLastSynced(): number {
  const raw = readString(AUTH_PROFILE_SYNC_KEY);
  const n = Number(raw || 0);
  return Number.isFinite(n) ? n : 0;
}

export function setProfileLastSynced(epochMs: number) {
  const n = Number(epochMs || 0);
  if (!Number.isFinite(n) || n <= 0) return;
  writeStringIfChanged(AUTH_PROFILE_SYNC_KEY, String(Math.floor(n)));
}

export function clearPendingContextSelection() {
  removeMany([AUTH_PENDING_CONTEXT_SELECTION_KEY, AUTH_SELECTION_TOKEN_KEY]);
}

export function setPendingContextSelection() {
  writeStringIfChanged(AUTH_PENDING_CONTEXT_SELECTION_KEY, "1");
}

export function setRequiresContextSelection() {
  writeStringIfChanged(AUTH_PENDING_CONTEXT_SELECTION_KEY, "1");
}

export function clearAllAuthStorage() {
  removeMany([
    AUTH_TOKEN_KEY,
    AUTH_CONTEXTS_KEY,
    AUTH_ACTIVE_CONTEXT_KEY,
    AUTH_CUSTOMER_KEY,
    AUTH_SELECTION_TOKEN_KEY,
    AUTH_PENDING_CONTEXT_SELECTION_KEY,
    AUTH_PROFILE_SYNC_KEY,
  ]);

  // Also clear selected company code used for App-Code header.
  // remove("app-code");
}
